import React, { useEffect, useState } from "react";
import { Loader2, Plus, ShieldCheck, Trash2, UserX, X } from "lucide-react";
import Button from "../components/ui/Button";
import { SelectField, TextField } from "../components/ui/Field";
import { Alert, EmptyState } from "../components/ui/Feedback";
import { userApi } from "../api/authApi";
import { useAuth } from "../app/AuthProvider";
import { formatDate } from "../lib/format";

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

function StatusPill({ active }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-micro font-semibold ${
        active
          ? "border-live/25 bg-live-soft text-live"
          : "border-dead/25 bg-dead-soft text-dead"
      }`}
    >
      {active ? "Active" : "Deactivated"}
    </span>
  );
}

/** Create-user overlay. Same fixed-card pattern as the results image modal. */
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "user",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const set = (key) => (event) =>
    setForm((f) => ({ ...f, [key]: event.target.value }));

  async function handleSubmit(event) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    setSubmitting(true);
    try {
      const { user } = await userApi.create(form);
      onCreated(user);
    } catch (err) {
      if (err.status === 400 && err.details) setErrors(err.details);
      else setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create a new user"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-4 backdrop-blur-sm"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-soft bg-white p-6 shadow-float"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink-900">
            New user
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {formError && (
          <div className="mb-4">
            <Alert tone="error">{formError}</Alert>
          </div>
        )}

        <div className="space-y-4">
          <TextField
            label="Email"
            type="email"
            required
            autoFocus
            value={form.email}
            onChange={set("email")}
            hint={errors.email}
          />
          <TextField
            label="Name (optional)"
            value={form.name}
            onChange={set("name")}
          />
          <TextField
            label="Temporary password"
            type="password"
            required
            value={form.password}
            onChange={set("password")}
            hint={
              errors.password ||
              "At least 10 characters. Share it with the user directly."
            }
          />
          <SelectField
            label="Role"
            value={form.role}
            onChange={set("role")}
            options={ROLE_OPTIONS}
          />
        </div>

        <div className="mt-6 flex justify-end gap-2.5">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Create user
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [state, setState] = useState({ loading: true, users: [], error: null });
  const [showCreate, setShowCreate] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [rowError, setRowError] = useState(null);

  async function load() {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await userApi.list();
      setState({ loading: false, users: data.users, error: null });
    } catch (err) {
      setState({ loading: false, users: [], error: err.message });
    }
  }

  useEffect(() => {
    load();
  }, []);

  function handleCreated(newUser) {
    setShowCreate(false);
    setState((s) => ({ ...s, users: [newUser, ...s.users] }));
  }

  async function toggleActive(target) {
    setRowError(null);
    setPendingId(target.id);
    try {
      const { user } = await userApi.update(target.id, {
        isActive: !target.isActive,
      });
      setState((s) => ({
        ...s,
        users: s.users.map((u) => (u.id === user.id ? user : u)),
      }));
    } catch (err) {
      setRowError(err.message);
    } finally {
      setPendingId(null);
    }
  }

  async function toggleRole(target) {
    setRowError(null);
    setPendingId(target.id);
    try {
      const nextRole = target.role === "admin" ? "user" : "admin";
      const { user } = await userApi.update(target.id, { role: nextRole });
      setState((s) => ({
        ...s,
        users: s.users.map((u) => (u.id === user.id ? user : u)),
      }));
    } catch (err) {
      setRowError(err.message);
    } finally {
      setPendingId(null);
    }
  }

  async function handleDelete(target) {
    if (!window.confirm(`Delete ${target.email}? This can't be undone.`))
      return;
    setRowError(null);
    setPendingId(target.id);
    try {
      await userApi.remove(target.id);
      setState((s) => ({
        ...s,
        users: s.users.filter((u) => u.id !== target.id),
      }));
    } catch (err) {
      setRowError(err.message);
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
            User management
          </h1>
          <p className="mt-1 max-w-reading text-sm text-ink-600">
            Create accounts for teammates and control who has admin access.
            There's no self-signup — every account starts here.
          </p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => setShowCreate(true)}
        >
          New user
        </Button>
      </div>

      {state.error && (
        <div className="mb-4">
          <Alert tone="error">{state.error}</Alert>
        </div>
      )}
      {rowError && (
        <div className="mb-4">
          <Alert tone="error">{rowError}</Alert>
        </div>
      )}

      {state.loading ? (
        <div className="flex justify-center py-16">
          <Loader2
            className="h-6 w-6 animate-spin text-ink-400"
            aria-hidden="true"
          />
        </div>
      ) : state.users.length === 0 ? (
        <EmptyState title="No users yet">
          Create the first account to get started.
        </EmptyState>
      ) : (
        <div className="overflow-hidden rounded-soft border border-ink-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead className="border-b border-ink-200 bg-paper-sunken/60">
                <tr>
                  {[
                    "User",
                    "Role",
                    "Status",
                    "Last sign-in",
                    "Created",
                    "",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2.5 text-left text-micro font-semibold text-ink-600"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.users.map((u) => {
                  const isSelf = u.id === currentUser?.id;
                  const busy = pendingId === u.id;

                  return (
                    <tr
                      key={u.id}
                      className="border-b border-ink-100 last:border-b-0"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-ink-900">
                          {u.name || u.email}
                        </p>
                        {u.name && (
                          <p className="text-micro text-ink-500">{u.email}</p>
                        )}
                        {isSelf && (
                          <span className="mt-0.5 inline-block rounded bg-[#8f5eeb]-100 px-1.5 py-0.5 text-micro font-semibold text-[#8f5eeb]-600">
                            You
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleRole(u)}
                          disabled={isSelf || busy}
                          className="inline-flex items-center gap-1 rounded border border-ink-200 px-2 py-0.5 text-micro font-semibold text-ink-700 transition-colors hover:border-ink-300 disabled:cursor-not-allowed disabled:opacity-50"
                          title={
                            isSelf
                              ? "You can't change your own role."
                              : "Click to toggle role"
                          }
                        >
                          {u.role === "admin" && (
                            <ShieldCheck
                              className="h-3 w-3 text-[#8f5eeb]-600"
                              aria-hidden="true"
                            />
                          )}
                          {u.role === "admin" ? "Admin" : "User"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill active={u.isActive} />
                      </td>
                      <td className="px-4 py-3 text-ink-600">
                        {u.lastLoginAt ? formatDate(u.lastLoginAt) : "Never"}
                      </td>
                      <td className="px-4 py-3 text-ink-600">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={UserX}
                            disabled={isSelf || busy}
                            onClick={() => toggleActive(u)}
                          >
                            {u.isActive ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            icon={Trash2}
                            disabled={isSelf || busy}
                            onClick={() => handleDelete(u)}
                            aria-label={`Delete ${u.email}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
